import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import ResponsiveDialog, {
  StyledButton,
  StyledLoadingButton,
} from '~/components/ResponsiveDialog';

interface CreateSnapshotModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (label: string) => void;
  isPending?: boolean;
}

const CreateSnapshotModal = ({
  open,
  onClose,
  onSubmit,
  isPending = false,
}: CreateSnapshotModalProps) => {
  const { t } = useTranslation('selfManagedHosting', {
    keyPrefix: 'snapshots.createModal',
  });

  const [snapshotLabel, setSnapshotLabel] = useState('');

  const handleClose = () => {
    setSnapshotLabel('');
    onClose();
  };

  const handleSubmit = () => {
    if (!snapshotLabel) return;
    onSubmit(snapshotLabel);
  };

  const handleLabelChange = (value: string) => {
    const trimmedLabel = value.trim();
    setSnapshotLabel(trimmedLabel);
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={handleClose}
      data-testid="create-snapshot-modal"
    >
      <DialogTitle>{t('title')}</DialogTitle>
      <Divider />
      <DialogContent>
        <Stack spacing={2}>
          <Typography>{t('description')}</Typography>
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="h4">{t('labelField.label')}</Typography>
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
              >
                {t('labelField.optional')}
              </Typography>
            </Stack>
            <TextField
              value={snapshotLabel}
              onChange={(e) => handleLabelChange(e.target.value)}
              placeholder={t('labelField.placeholder')}
              inputProps={{ 'data-testid': 'snapshot-label-input' }}
              fullWidth
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <StyledButton
          variant="text"
          onClick={handleClose}
          data-testid="create-snapshot-cancel-btn"
        >
          {t('cancelBtn')}
        </StyledButton>
        <StyledLoadingButton
          variant="contained"
          loading={isPending}
          disabled={!snapshotLabel}
          onClick={handleSubmit}
          sx={{ flexBasis: '9.5rem' }}
          data-testid="create-snapshot-submit-btn"
        >
          {t('submitBtn')}
        </StyledLoadingButton>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default CreateSnapshotModal;
